import { dateLib, ticketLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { Suspense, useState } from "react"
import { ArrowLeft, Home } from "react-feather"
import { useNavigate, useParams } from "react-router-dom"
import { type TicketGet200Ticket, ticketApi } from "@/api/client/ticket"
import type { TicketGetParams } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { PaymentMethodRequiredDialog } from "@/components/payment/PaymentMethodRequiredDialog"
import {
  PlanStartDialog,
  RegisteredTenantWarningDialog,
  TenantApplicationApprovalDialog,
  TenantApplicationRejectionDialog,
  useTenantApplicationApprovalFlow,
} from "@/components/tenant-application"
import {
  type ContractorInfoData,
  ContractorInfoDialog,
} from "@/components/tickets/ContractorInfoDialog"
import { type StatusChangeData, StatusChangeDialog } from "@/components/tickets/StatusChangeDialog"
import { TicketChat } from "@/components/tickets/TicketChat"
import { TicketCommentCard } from "@/components/tickets/TicketCommentCard"
import { TicketDetailTabs } from "@/components/tickets/TicketDetailTabs"
import { TicketInfoSection } from "@/components/tickets/TicketInfoSection"
import { TicketNotificationBar } from "@/components/tickets/TicketNotificationBar"
import { TicketStatusDropdown } from "@/components/tickets/TicketStatusDropdown"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner/Spinner"
import { toast } from "@/hooks/use-toast"
import { usePaymentMethodRequirement } from "@/hooks/usePaymentMethodRequirement"

export function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState("history")
  const [isContractorDialogOpen, setIsContractorDialogOpen] = useState(false)
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false)
  const {
    ensurePaymentMethod,
    isCheckingPaymentMethod,
    isPaymentMethodDialogOpen,
    setIsPaymentMethodDialogOpen,
  } = usePaymentMethodRequirement()

  const queryParams: TicketGetParams = {
    withTicketComments: "true",
    withTicketCommentsContactFromOwner: "true",
    withTicketCommentsContactFromTenant: "true",
    withTicketCommentsContactFromAdmin: "true",
    withTicketCommentsContactToOwner: "true",
    withTicketCommentsContactToTenant: "true",
    withTicketCommentsContactToAdmin: "true",
    withProperty: "true",
    withRoom: "true",
    withTenant: "true",
    withOwner: "true",
  }

  const { data, isLoading, error, refetch } = ticketApi.useTicketGet(ticketId || "0", queryParams, {
    query: {
      enabled: !!ticketId,
    },
  })

  // 入居者情報承認フロー
  const approvalFlow = useTenantApplicationApprovalFlow({
    ticketId,
    roomId: data?.ticket?.room?.id,
    onComplete: () => {
      // 承認/棄却完了後にチケット情報を再取得
      refetch()
    },
  })

  const handleApproveTenantApplication = async () => {
    if (approvalFlow.hasConflictingRegisteredTenant) {
      approvalFlow.handleApprove()
      return
    }
    if (await ensurePaymentMethod()) {
      approvalFlow.handleApprove()
    }
  }

  const updateStatusMutation = ticketApi.useTicketUpdate({
    mutation: {
      onSuccess: () => {
        refetch()
        toast({
          title: "ステータスを更新しました",
        })
      },
      onError: (error) => {
        toast({
          title: "ステータスの更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        })
      },
    },
  })

  const updateContractorMutation = ticketApi.useTicketUpdateContractor({
    mutation: {
      onSuccess: () => {
        refetch()
        toast({
          title: "業者依頼情報を更新しました",
        })
      },
      onError: (error) => {
        toast({
          title: "業者依頼情報の更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        })
      },
    },
  })

  const handleStatusChange = () => {
    if (!ticketId) return

    // ダイアログを開いてステータス変更理由を入力
    setIsStatusDialogOpen(true)
  }

  const handleStatusChangeConfirm = (data: StatusChangeData) => {
    if (!ticketId) return

    updateStatusMutation.mutate({
      id: ticketId,
      data: {
        status: data.newStatus,
        reason: data.reason,
      },
    })

    // ダイアログを閉じる
    setIsStatusDialogOpen(false)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-slate-500">読み込み中...</div>
      </div>
    )
  }

  if (error || !data?.ticket) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-red-500">チケット情報の取得に失敗しました</div>
      </div>
    )
  }

  const ticket: TicketGet200Ticket = data.ticket
  const propertyInfo = ticket.property ?? undefined
  const roomInfo = ticket.room ?? undefined
  const propertyName = propertyInfo?.name || ""
  const roomName = roomInfo?.name || ""
  const location = [propertyName, roomName].filter(Boolean).join(" ")

  const historyContent = (
    <>
      {ticket.ticketComments && ticket.ticketComments.length > 0 ? (
        [...ticket.ticketComments]
          .sort((a, b) => {
            const contactAtDiff = new Date(a.contactAt).getTime() - new Date(b.contactAt).getTime()
            return contactAtDiff || a.id - b.id
          })
          .map((comment, index) => {
            const fromName = ticketLib.getTicketContactName(comment, "from")
            const toName = ticketLib.getTicketContactName(comment, "to")
            const title = ticketLib.getContactTypeLabel(comment.contactType)

            return (
              <TicketCommentCard
                key={comment.id}
                progressNumber={index + 1}
                comment={comment}
                fromName={fromName}
                toName={toName}
                title={title}
              />
            )
          })
      ) : (
        <div className="text-center py-8 text-slate-500">対応履歴はありません</div>
      )}
    </>
  )

  const overviewContent = (
    <div className="space-y-4">
      {/* 通知エリア */}
      <TicketNotificationBar
        message="業者対応状況が変更可能です"
        actionLabel="確認する"
        onAction={() => {
          setIsContractorDialogOpen(true)
        }}
      />

      {/* 案件管理情報 */}
      <TicketInfoSection
        title="案件管理情報"
        items={[
          { label: "案件ID", value: ticket.id?.toString() || "-" },
          { label: "案件タイトル", value: ticket.title || "-" },
          {
            label: "案件カテゴリ",
            value: `${ticketLib.getCategoryLabel(ticket.category || "")} / ${ticketLib.getSubCategoryLabel(ticket.subCategory || "")}`,
          },
          { label: "発生日時", value: formatDate(ticket.occurredAt || "-") },
        ]}
      />

      {/* 契約情報 */}
      <TicketInfoSection
        title="契約情報"
        items={[
          { label: "物件名", value: propertyInfo?.name || "-" },
          { label: "部屋番号", value: roomInfo?.name || "-" },
          { label: "賃借人", value: ticket.tenant?.name || "-" },
          {
            label: "電話番号",
            value: ticket.tenant
              ? formatPhone(ticket.tenant.phone1, ticket.tenant.phone2, ticket.tenant.phone3)
              : "-",
          },
          // {
          //   label: "加入プラン",
          //   value:
          //     (ticket.room?.activePlan as { planMaster?: { name?: string } } | null)?.planMaster
          //       ?.name || "-",
          // },
          {
            label: "契約期間",
            value: ticket.tenant?.startDate ? `${formatDate(ticket.tenant?.startDate)} ~` : "-",
          },
        ]}
      />

      {/* 業者依頼情報 */}
      <TicketInfoSection
        title="業者依頼情報"
        items={[
          {
            label: "業者対応状況",
            value: (
              <>
                {ticket.contractorStatus ? (
                  <Badge className="bg-yellow-500 text-white hover:bg-yellow-600">
                    {ticketLib.getContractorStatusLabel(ticket.contractorStatus)}
                  </Badge>
                ) : (
                  "-"
                )}
              </>
            ),
          },
          { label: "手配先業者", value: ticketLib.getContractorTypeLabel(ticket.contractorType) },
          { label: "業者名", value: ticket.contractorName || "-" },
          { label: "業者担当者", value: ticket.contractorContact || "-" },
          { label: "業者連絡先（電話）", value: ticket.contractorContactInfo || "-" },
          { label: "業者対応予定日", value: ticket.contractorScheduledDate || "-" },
          { label: "備考", value: ticket.contractorNote || "-" },
        ]}
        actionLabel="変更する"
        onAction={() => {
          setIsContractorDialogOpen(true)
        }}
      />
    </div>
  )

  const chatContent = ticketId ? (
    <Suspense
      fallback={
        <div className="flex justify-center items-center h-full">
          <Spinner />
        </div>
      }
    >
      <TicketChat ticketId={ticketId} ownerName={ticket.owner?.name || "オーナー"} />
    </Suspense>
  ) : null

  return (
    <div className="flex flex-col">
      <div className="mb-6 space-y-4 flex-shrink-0">
        <div className="grid grid-cols-[auto_1fr_auto] gap-1 items-center justify-between">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="gap-1.5">
            <ArrowLeft className="h-4 w-4" />
            戻る
          </Button>
          <div className="min-w-0">
            <h1
              className="text-sm font-bold text-slate-900 mb-1 text-ellipsis overflow-hidden whitespace-nowrap"
              title={ticket.title}
            >
              {ticket.title}
            </h1>
            {location && (
              <div className="flex items-center gap-1 text-xs">
                <Home className="h-3 w-3" />
                <span>{location}</span>
              </div>
            )}
          </div>
          <TicketStatusDropdown
            currentStatus={ticket.status}
            onStatusChange={handleStatusChange}
            disabled={updateStatusMutation.isPending}
          />
        </div>
      </div>

      <TicketDetailTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        historyContent={historyContent}
        overviewContent={overviewContent}
        chatContent={chatContent}
      />

      <ContractorInfoDialog
        open={isContractorDialogOpen}
        onOpenChange={setIsContractorDialogOpen}
        defaultValues={{
          contractorStatus: ticket.contractorStatus || undefined,
          contractorType: ticket.contractorType || undefined,
          contractorName: ticket.contractorName || undefined,
          contractorContact: ticket.contractorContact || undefined,
          contractorContactInfo: ticket.contractorContactInfo || undefined,
          contractorScheduledDate: ticket.contractorScheduledDate
            ? new Date(ticket.contractorScheduledDate)
            : undefined,
          contractorNote: ticket.contractorNote || undefined,
        }}
        onSave={async (data: ContractorInfoData) => {
          if (!ticketId) return
          await updateContractorMutation.mutateAsync({
            id: ticketId,
            data: {
              contractorStatus: data.contractorStatus,
              contractorType: data.contractorType,
              contractorName: data.contractorName,
              contractorContact: data.contractorContact,
              contractorContactInfo: data.contractorContactInfo,
              contractorScheduledDate: data.contractorScheduledDate
                ? dateLib.formatDateISO(data.contractorScheduledDate)
                : null,
              contractorNote: data.contractorNote,
            },
          })
        }}
        propertyId={ticket.propertyId}
        loading={updateContractorMutation.isPending}
      />

      <StatusChangeDialog
        open={isStatusDialogOpen}
        onOpenChange={setIsStatusDialogOpen}
        currentStatus={ticket.status}
        onSave={handleStatusChangeConfirm}
      />

      {/* 入居者情報承認フロー モーダル群 */}
      <TenantApplicationApprovalDialog
        open={approvalFlow.isViewDialogOpen}
        onOpenChange={(open) => {
          if (!open) approvalFlow.closeApprovalFlow()
        }}
        tenantApplication={approvalFlow.tenantApplication}
        room={approvalFlow.room}
        files={approvalFlow.files}
        loading={approvalFlow.loading || isCheckingPaymentMethod}
        onApprove={handleApproveTenantApplication}
        onReject={approvalFlow.handleReject}
      />

      <TenantApplicationRejectionDialog
        open={approvalFlow.isRejectDialogOpen}
        onOpenChange={(open) => {
          if (!open) approvalFlow.handleBack()
        }}
        onSubmit={approvalFlow.handleRejectionSubmit}
        loading={approvalFlow.loading}
      />

      <PlanStartDialog
        open={approvalFlow.isPlanStartDialogOpen}
        onOpenChange={(open) => {
          if (!open) approvalFlow.handleBack()
        }}
        onSubmit={approvalFlow.handlePlanStartSubmit}
        onBack={approvalFlow.handleBack}
        loading={approvalFlow.loading}
        tenantApplication={approvalFlow.tenantApplication}
        room={approvalFlow.room}
      />
      <RegisteredTenantWarningDialog
        open={approvalFlow.isRegisteredTenantWarningDialogOpen}
        onOpenChange={(open) => {
          if (!open) approvalFlow.handleBack()
        }}
        roomDetailPath={approvalFlow.roomDetailPath}
      />
      <PaymentMethodRequiredDialog
        open={isPaymentMethodDialogOpen}
        onOpenChange={setIsPaymentMethodDialogOpen}
      />
    </div>
  )
}

function formatDate(dateString: string): string {
  return dateLib.formatDateLocale(dateString)
}
